// import components
import {
  ControlledCheckbox,
  ControlledSelect
} from 'components/shared/ControlledFields';
import ColorPickerPopover from 'components/shared/ColorPickerPopover';
import TextField from 'components/shared/ControlledFields/ControlledTextField';

// import hooks
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'hooks';

// import queries
import { useGetBrCategoryOptions, useGetBrTypeOptions } from 'queries';

// import react
import React from 'react';

// import types
import { HexColor } from 'components/shared/ColorPickerPopover/ColorPickerPopover.types';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';

// import utils
import { shouldHideSortOrder } from '../utils/utils';

// import enums
import { PlanUnitType } from 'components/enums';

export const TypeField: React.FC = () => {
  const { data: brTypeOptions } = useGetBrTypeOptions();
  const { t } = useTranslation('common');
  const { control } = useFormContext();
  const hasRegionalPoolOrganisationAccess = useSelector(
    (state) => state.grants.hasRegionalPoolOrganisationAccess
  );

  const filteredBrTypeOptions = hasRegionalPoolOrganisationAccess
    ? brTypeOptions
    : (brTypeOptions as Option[])?.filter(
        (option) => option.value !== PlanUnitType.REGIONAL_POOL
      );

  return (
    <ControlledSelect
      control={control}
      variant="outlined"
      label={t('costCenters.grid.type')}
      name="type"
      options={filteredBrTypeOptions as Option[]}
    />
  );
};

export const CategoryField: React.FC = () => {
  const { data: brCategoryOptions } = useGetBrCategoryOptions();
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledSelect
      control={control}
      variant="outlined"
      label={t('costCenters.grid.category')}
      name="category"
      options={brCategoryOptions as Option[]}
    />
  );
};

export const SortOrderField: React.FC = () => {
  const { t } = useTranslation('common');
  const { getValues } = useFormContext();
  const { control } = useFormContext();

  if (shouldHideSortOrder(getValues('type'))) {
    return <></>;
  }

  return (
    <TextField
      control={control}
      variant="outlined"
      name="sortOrderAgency"
      label={t('costCenters.grid.sortOrderAgency')}
    />
  );
};

export const RequestExcludeField: React.FC<{ disabled?: boolean }> = (
  props
) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      {...props}
      control={control}
      name={`REQUEST_EXCLUDE`}
      label={t('costCenters.grid.flags.REQUEST_EXCLUDE')}
    />
  );
};

export const ColorField: React.FC = () => {
  const { t } = useTranslation('common');
  const { setValue } = useFormContext();
  const { control } = useFormContext();
  const onColorSelect = (color: HexColor) => {
    setValue('colorCode', color, { shouldDirty: true });
  };

  return (
    <Controller
      name="colorCode"
      control={control}
      render={({ field }) => (
        <ColorPickerPopover
          color={field.value}
          onColorSelected={onColorSelect}
          hasEditFeature={true}
          label={t('color')}
        />
      )}
    />
  );
};

export const PrivilegedEmailField: React.FC = () => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      variant="outlined"
      name="privilegedEmailAddress"
      label={t('costCenters.grid.privilegedEmailAddress')}
    />
  );
};
